import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import I18n from '../../../config/i18n';
import ReviewForm from './ReviewForm';
import ReviewTravelDates from './ReviewTravelDates';
import ReviewConfirmation from './ReviewConfirmation';

import './reviews.css';

interface Booking {
  accommodationTitle: string;
  arrival: Date;
  departure: Date;
  firstName: string;
}

interface Ratings {
  ratingArea: number;
  ratingComfort: number;
  ratingValue: number;
  ratingCleanliness: number;
}

interface ReviewsCreateProps {
  workflowStatus?: string;
  invitationCode?: string;
  booking?: Booking;
  ratings: Ratings;
  reviewText: string;
  npsScore: number;
  onDoStaging: () => void;
  onCorrectReview: () => void;
  handleReviewSubmit: () => void;
  lang: string;
}

interface CreateFormProps {
  booking: Booking;
  ratings: Ratings;
  npsScore: number;
  workflowStatus: string;
  reviewText: string;
  validationMode: boolean;
  setValidationMode: (validationMode: boolean) => void;
  onDoStaging: () => void;
}

function CreateForm({
  booking,
  ratings,
  npsScore,
  workflowStatus,
  reviewText,
  validationMode,
  setValidationMode,
  onDoStaging,
}: CreateFormProps): JSX.Element {
  function showValidationMode(): boolean {
    return validationMode && workflowStatus === 'create';
  }

  return (
    <div>
      {showValidationMode() ? (
        <p className="reviews__error">{I18n.t('reviews.validation_error')}</p>
      ) : (
        <div className="reviews__intro">
          <p>{I18n.t('reviews.intro_1')}</p>
          <p>{I18n.t('reviews.intro_2')}</p>
        </div>
      )}
      <ReviewTravelDates booking={booking} />
      <ReviewForm
        ratings={ratings}
        reviewText={reviewText}
        npsScore={npsScore}
        validationMode={validationMode}
        setValidationMode={setValidationMode}
        onDoStaging={onDoStaging}
      />
    </div>
  );
}

export default function ReviewsCreate({
  invitationCode,
  booking,
  ratings,
  reviewText,
  npsScore,
  workflowStatus,
  onDoStaging,
  onCorrectReview,
  handleReviewSubmit,
  lang,
}: ReviewsCreateProps): JSX.Element | null {
  const [validationMode, setValidationMode] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (workflowStatus === 'submitted') {
      navigate(`/${lang}/reviews/success`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowStatus]);

  if (!invitationCode || !booking || !workflowStatus) {
    return null;
  }

  return (
    <div>
      <div className="container reviews__container">
        <div className="row">
          <div className="col-lg-2 col-md-1" />
          <div className="col col-lg-8 col-md-10">
            <div className="reviews-body-col">
              <h1 className="reviews-title">{I18n.t('reviews.title')}</h1>
              {workflowStatus === 'create' ? (
                <CreateForm
                  booking={booking}
                  ratings={ratings}
                  npsScore={npsScore}
                  workflowStatus={workflowStatus}
                  reviewText={reviewText}
                  validationMode={validationMode}
                  setValidationMode={setValidationMode}
                  onDoStaging={onDoStaging}
                />
              ) : null}
              {workflowStatus === 'confirm' || workflowStatus === 'submitPending' ? (
                <ReviewConfirmation
                  lang={lang}
                  invitationCode={invitationCode}
                  booking={booking}
                  ratings={ratings}
                  reviewText={reviewText}
                  npsScore={npsScore}
                  onCorrectReview={onCorrectReview}
                  handleReviewSubmit={handleReviewSubmit}
                />
              ) : null}
            </div>
          </div>
          <div className="col-lg-2 col-md-1" />
        </div>
      </div>
    </div>
  );
}

const defaultProps: Pick<ReviewsCreateProps, 'workflowStatus' | 'invitationCode' | 'booking'> = {
  workflowStatus: undefined,
  invitationCode: undefined,
  booking: undefined,
};

ReviewsCreate.defaultProps = defaultProps;
